import "core-js/stable";
import "regenerator-runtime/runtime";
import { initializeIcons } from "@uifabric/icons";
import "./styles.less";
import { OfficeRoamingSettings } from "./services/OfficeRoamingSettings";
import { EntryPoint } from "./services/EntryPoint";
import LoginDialog from "./components/login/LoginDialog";
import { AnalyticsManager } from "./services/AnalyticsManager";
import { NewformaApiClient } from "./services/NewformaApi/NewformaApiClient";
import { AWSSignatureVersion4Provider } from "./services/AWSSignatureVersion4Provider";
import { Authenticator } from "./services/Authenticator";
import { UrlBuilder } from "./services/UrlBuilder";
import { UnauthenticatedNewformaApiClient } from "./services/UnauthenticatedNewformaApiClient";
import { SmartFilingManager } from "./services/SmartFiling/SmartFilingManager";
import { ConfigurationService } from "./services/ConfigurationService";
import { OfficeWrapper } from "./services/OfficeWrapper";
import { AuthUrlProvider } from "./services/AuthUrlProvider";
import { HttpRequestWrapper } from "./services/HttpRequestWrapper";
import { OfficeNotificationService } from "./services/officeUi/OfficeNotificationService";
import { UserDisplayService } from "./services/UserDisplayService";
import { OutlookApiService } from "./services/OutlookApiService";
import { ActionItemApiService } from "./services/NewformaApi/ActionItemApiService";
import { FileUploadApiService } from "./services/NewformaApi/FileUploadApiService";
import { NrnServiceWrapper } from "./services/NrnServiceWrapper";
import { StorageWrapper } from "./services/StorageWrapper";
import { ConfigurationsApiService } from "./services/NewformaApi/ConfigurationsApiService";
import { WindowWrapper } from "./services/WindowWrapper";
import { FormValidationHelpers } from "./helpers/FormValidationHelpers";
import { TranslationService } from "./services/TranslationService";
import { Logger, Log } from "./services/Logger";
import { CustomerSupportEmailService } from "./services/CustomerSupportEmailService";
import { ProjectsApiService } from "./services/NewformaApi/ProjectsApiService";
import { SendAndFileHelpers } from "./helpers/SendAndFile/SendAndFileHelpers";
import { DateHelpers } from "./helpers/DateHelpers";
import { SubmittalsApiService } from "./services/NewformaApi/SubmittalsApiService";
import { EulaApiService } from "./services/NewformaApi/EulaApiService";
import { RfiApiService } from "./services/NewformaApi/RfiApiService";
import { EmailApiService } from "./services/NewformaApi/EmailApiService";
import { MsGraphTokenService } from "./services/MsGraphTokenService";
import { MsGraphApiService } from "./services/MsGraphApiService";
import { GraphRequestWrapper } from "./services/GraphRequestWrapper";
import { ClearMessageService } from "./services/ClearMessageService";
import { AuthApiService } from "./services/NewformaApi/AuthApiService";
import { ProjectsCacheService } from "./services/NewformaApi/ProjectsCacheService";
import { IndexedDbHelper } from "./helpers/IndexedDbHelper";
import { InvalidateCacheService } from "./services/NewformaApi/InvalidateCacheService";
import { ProjectsRetryService } from "./services/NewformaApi/ProjectsRetryService";
import { RefreshProjectsBackgroundTask } from "./services/NewformaApi/RefreshProjectsBackgroundTask";
import { ProjectsPagingErrorService } from "./services/NewformaApi/ProjectsPagingErrorService";
import { Messenger } from "./services/Messenger";
import { IProjectsService } from "./services/NewformaApi/IProjectsService";
import { FileTransferApiService } from "./services/NewformaApi/FileTransferApiService";
import GainSight from "./services/GainSight";
import { SmartFilingManagerFluent9 } from "./services/SmartFiling/SmartFilingManagerFluent9";
import { SharePointAuthServiceFactory } from "./services/SharePoint/SharePointAuthServiceFactory";
import { SharePointConsentRedirectService } from "./services/SharePoint/SharePointConsentRedirectService";
import { LocalStorageKeys } from "./models/StorageKeys";

initializeIcons("./assets/fonts/"); // Function needed to initialize Fabric icons
// Extend global object using typescript declaration-merging
declare global {
    var __COMMIT_HASH__: string;
    var __BRANCH__: string;
    var __ENV__: string;
    var __MANIFEST_ID__: string;
}

Office.initialize = () => {
    // NOTE: Keep - required. This also fires before .async()
};
// tslint:disable-next-line
(async () => {
    await Office.onReady();
    const logger = new Logger();
    const requestWrapper = new HttpRequestWrapper();
    const nrnServiceWrapper = new NrnServiceWrapper();
    const analyticsManager = new AnalyticsManager();
    const officeNotificationService = new OfficeNotificationService();
    const formValidationHelpers = new FormValidationHelpers();
    const userDisplayService = new UserDisplayService();
    const dateHelpers = new DateHelpers();
    const windowWrapper = new WindowWrapper(logger);
    const config = new ConfigurationService(logger);
    const storageWrapper = new StorageWrapper(logger);
    const officeWrapper = new OfficeWrapper(logger);
    const translationService = new TranslationService(logger);
    const aws = new AWSSignatureVersion4Provider(logger);
    const urlBuilder = new UrlBuilder(config);
    const officeRoamingSettings = new OfficeRoamingSettings(officeWrapper, logger);
    const loginDialog = new LoginDialog(officeWrapper, config, windowWrapper, logger);
    const unauthenticatedNewformaApiClient = new UnauthenticatedNewformaApiClient(config, requestWrapper, logger);
    const smartFilingManager = new SmartFilingManager(officeRoamingSettings, officeWrapper, logger);
    const smartFilingManagerFluent9 = new SmartFilingManagerFluent9(officeWrapper, officeRoamingSettings, logger);
    const authUrlProvider = new AuthUrlProvider(
        unauthenticatedNewformaApiClient,
        urlBuilder,
        officeWrapper,
        storageWrapper,
        windowWrapper
    );
    const dbName = "newformaCache";
    const dbVersion = 1;
    const dbStoreName = "projectsStorage";
    const indexedDbHelper = new IndexedDbHelper(dbName, dbVersion, dbStoreName, logger);
    const maxCacheLifetimeMs = 14 * 24 * 3600000; // 14 days
    const invalidateCacheService = new InvalidateCacheService(
        indexedDbHelper,
        window.localStorage,
        logger,
        maxCacheLifetimeMs
    );

    // Auth tokens are stored in localStorage only (roaming settings has a 32KB limit
    // that is easily exceeded by JWT tokens + AWS credentials).
    const authenticator = new Authenticator(
        loginDialog,
        storageWrapper,
        unauthenticatedNewformaApiClient,
        authUrlProvider,
        officeWrapper,
        analyticsManager,
        logger
    );
    try {
        // Dialog windows can't identify the user; on classic desktop they share localStorage with
        // the task pane, so logging out from one would wipe the task pane's session.
        if (officeWrapper.hasMailboxContext() && !authenticator.isValidUser()) {
            logger.info("User is invalid, calling authenticator.logoutFromApp()");
            authenticator.logoutFromApp();
        }
    } catch (error) {
        logger.error("Cannot validate the user", error);
    }
    const apiClient = new NewformaApiClient(config, aws, authenticator, logger);
    const msGraphTokenService = new MsGraphTokenService(storageWrapper, apiClient, requestWrapper, logger);

    const graphRequestWrapper = new GraphRequestWrapper(requestWrapper, msGraphTokenService);
    const clearMessageService = new ClearMessageService();

    const msGraphApiService = new MsGraphApiService(officeWrapper, graphRequestWrapper, clearMessageService, logger);
    const outlookApiService = new OutlookApiService(officeWrapper, windowWrapper, msGraphApiService, logger);
    const fileUploadService = new FileUploadApiService(apiClient, requestWrapper, logger);
    const configurationsApiService = new ConfigurationsApiService(
        apiClient,
        unauthenticatedNewformaApiClient,
        requestWrapper,
        logger
    );
    const customerSupportEmailService = new CustomerSupportEmailService(logger, officeWrapper, windowWrapper, config);
    const projectsApiService = new ProjectsApiService(
        logger,
        apiClient,
        userDisplayService,
        requestWrapper,
        nrnServiceWrapper,
        officeRoamingSettings,
        storageWrapper
    );
    const emailApiService = new EmailApiService(
        logger,
        apiClient,
        requestWrapper,
        analyticsManager,
        officeRoamingSettings,
        officeWrapper,
        smartFilingManager
    );
    const actionItemApiService = new ActionItemApiService(
        apiClient,
        requestWrapper,
        outlookApiService,
        fileUploadService,
        nrnServiceWrapper,
        logger,
        emailApiService
    );
    const projectsPagingErrorService = new ProjectsPagingErrorService(projectsApiService, logger);
    const projectsRetryService = new ProjectsRetryService(projectsPagingErrorService, logger);
    const projectCacheService = new ProjectsCacheService(
        projectsRetryService,
        logger,
        indexedDbHelper,
        maxCacheLifetimeMs
    );
    const messenger = new Messenger();
    /* tslint:disable-next-line:no-floating-promises */
    clearLeftoversAndRefreshProjectCache(
        projectsRetryService,
        logger,
        indexedDbHelper,
        invalidateCacheService,
        messenger,
        officeRoamingSettings
    );
    const sendAndFileHelpers = new SendAndFileHelpers(officeWrapper, logger, projectCacheService);
    const submittalsApiService = new SubmittalsApiService(
        apiClient,
        requestWrapper,
        outlookApiService,
        fileUploadService,
        analyticsManager,
        emailApiService,
        nrnServiceWrapper
    );
    const eulaApiService = new EulaApiService(apiClient, requestWrapper, logger);
    const rfiApiService = new RfiApiService(
        logger,
        apiClient,
        requestWrapper,
        analyticsManager,
        outlookApiService,
        fileUploadService,
        emailApiService,
        nrnServiceWrapper
    );
    const fileTransferService = new FileTransferApiService(
        logger,
        apiClient,
        requestWrapper,
        analyticsManager,
        outlookApiService,
        fileUploadService
    );

    const authApiService = new AuthApiService(apiClient, requestWrapper);

    // Decided once per load and shared by the factory and EntryPoint: NAA (Office brokers MSAL
    // in-process) vs. the MSAL-only Office Dialog fallback. Both must always agree.
    const forceMsalFallback = storageWrapper.loadLocalStorage(LocalStorageKeys.forceSharePointMsalFallback) === "true";
    const sharePointAuthUsesNaa = !forceMsalFallback && officeWrapper.isNestedAppAuthSupported();
    logger.info(
        `SharePoint auth: NestedAppAuth 1.1 ${
            officeWrapper.isNestedAppAuthSupported() ? "" : "not "
        }supported by this host${forceMsalFallback ? " (fallback forced via localStorage)" : ""} - using ${
            sharePointAuthUsesNaa ? "NAA" : "the Office Dialog fallback"
        }`
    );
    const sharePointAuthServiceFactory = new SharePointAuthServiceFactory(
        officeWrapper,
        config,
        requestWrapper,
        logger,
        analyticsManager,
        sharePointAuthUsesNaa
    );
    const sharePointConsentRedirectService = new SharePointConsentRedirectService(storageWrapper, config, logger);

    const entryPoint = new EntryPoint(
        authenticator,
        apiClient,
        unauthenticatedNewformaApiClient,
        analyticsManager,
        smartFilingManager,
        smartFilingManagerFluent9,
        officeWrapper,
        officeRoamingSettings,
        officeNotificationService,
        config,
        actionItemApiService,
        storageWrapper,
        configurationsApiService,
        windowWrapper,
        formValidationHelpers,
        translationService,
        customerSupportEmailService,
        logger,
        projectCacheService,
        sendAndFileHelpers,
        dateHelpers,
        submittalsApiService,
        eulaApiService,
        rfiApiService,
        emailApiService,
        projectsApiService,
        nrnServiceWrapper,
        msGraphApiService,
        authApiService,
        invalidateCacheService,
        messenger,
        fileTransferService,
        outlookApiService,
        sharePointAuthServiceFactory,
        sharePointConsentRedirectService,
        sharePointAuthUsesNaa
    );

    if ((module as any).hot) {
        (module as any).hot.accept("./components/app/AppComponent", () => {
            entryPoint.render();
        });
    }
})();

async function clearLeftoversAndRefreshProjectCache(
    projectsService: IProjectsService,
    logger: Logger,
    indexedDbHelper: IndexedDbHelper,
    invalidateCacheService: InvalidateCacheService,
    messenger: Messenger,
    officeRoamingSettings: OfficeRoamingSettings
): Promise<void> {
    await invalidateCacheService.clearAllLeftovers();
    const maxCacheLifetimeForBackgroundRefreshMs = 3600000; // 1h
    const projectCacheForBackgroundService = new ProjectsCacheService(
        projectsService,
        logger,
        indexedDbHelper,
        maxCacheLifetimeForBackgroundRefreshMs
    );
    const cacheRefreshIntervalMs = 300000; // 5 minutes
    const refreshProjectsBackgroundService = new RefreshProjectsBackgroundTask(
        projectCacheForBackgroundService,
        logger,
        cacheRefreshIntervalMs,
        messenger,
        officeRoamingSettings
    );
    await refreshProjectsBackgroundService.init();
}
// The console Logs below should be left in place
window.onerror = function(message, source, lineno, colno, error) {
    // Log the error to your logging service if defined, otherwise console log it
    if (Log) {
        try {
            // if there is a stack... the message would appear twice... so instead set to a null
            Log.infoStyle(`Global Error Captured: ${error && error.stack ? "" : message}`, "red");
            const stack = error && error.stack ? error.stack : "No stack trace available";
            Log.infoStyle(`Stack: ${stack}`, "red");
            Log.infoStyle(`from >>  Source : ${source} LineNo: ${lineno}  ColNo: ${colno}`, "red");
        } catch {}
    }
    {
        console.error("Global Error Captured:", message);
    }

    // Return true to prevent the browser's default error overlay
    return true;
};

window.onunhandledrejection = function(event) {
    // Prevent the "black screen" crash ,save to Log 'if defined' otherwise console log the error
    if (Log) {
        try {
            Log.infoStyle(`Unhandled Promise Rejection: ${JSON.stringify(event)}`, "blue");
        } catch {}
    } else {
        console.error("Unhandled Promise Rejection:", event.reason);
    }
    event.preventDefault();
};

export * from "./index";
